import { tokens } from '@/design';
import { DEFAULT_SCALE_BAR_TEXT_SYMBOL } from './layoutDocument';
import {
  scaleBarElementLayout,
  scaleBarLayout,
  type ScaleBarGeometry,
} from './scaleBarGeometry';
import { scaleBarStyleMeta } from './scaleBarStyles';
import type {
  ScaleBarElement,
  ScaleBarStyleId,
  ScaleBarTextSymbol,
  ScaleBarUnits,
} from './layoutTypes';

export interface LayoutScaleBarProps {
  readonly styleId: ScaleBarStyleId;
  readonly units: ScaleBarUnits;
  readonly scaleDenominator: number | null;
  readonly maxWidthMm: number;
  readonly element?: ScaleBarElement;
  /** Element height; keeps the view box aspect equal to the element's. */
  readonly heightMm?: number;
}

const VIEW_W = 200;
const VIEW_H = 44;

interface BarShape {
  readonly geom: ScaleBarGeometry;
  readonly barW: number;
  readonly ink: string;
  readonly paper: string;
  readonly text: ScaleBarTextSymbol;
  readonly ptScale: number;
}

function displayText(value: string, text: ScaleBarTextSymbol): string {
  if (text.textCase === 'uppercase' || text.textCase === 'smallCaps') {
    return value.toUpperCase();
  }
  if (text.textCase === 'lowercase') return value.toLowerCase();
  return value;
}

function ScaleText({
  shape,
  x,
  y,
  value,
  defaultAnchor = 'middle',
}: {
  readonly shape: BarShape;
  readonly x: number;
  readonly y: number;
  readonly value: string;
  readonly defaultAnchor?: 'start' | 'middle' | 'end';
}) {
  const text = shape.text;
  const shown = displayText(value, text);
  const anchor =
    text.horizontalAlignment === 'center'
      ? 'middle'
      : text.horizontalAlignment === 'right'
        ? 'end'
        : text.horizontalAlignment === 'justify'
          ? 'middle'
          : defaultAnchor;
  const dominantBaseline =
    text.verticalAlignment === 'top'
      ? 'hanging'
      : text.verticalAlignment === 'middle'
        ? 'middle'
        : text.verticalAlignment === 'bottom'
          ? 'text-after-edge'
          : 'alphabetic';
  const adjustment =
    text.positionAdjustment === 'superscript'
      ? -text.sizePt * 0.35
      : text.positionAdjustment === 'subscript'
        ? text.sizePt * 0.25
        : 0;
  const tx = x + text.offsetXPt * shape.ptScale;
  const ty = y + (text.offsetYPt + adjustment) * shape.ptScale;
  const weight =
    text.fontStyle === 'bold' || text.fontStyle === 'boldItalic'
      ? tokens.font.weight.bold
      : tokens.font.weight.regular;
  const fontStyle =
    text.fontStyle === 'italic' || text.fontStyle === 'boldItalic'
      ? 'italic'
      : 'normal';
  const decoration = [
    text.underline ? 'underline' : '',
    text.strikethrough ? 'line-through' : '',
  ].filter(Boolean).join(' ') || undefined;
  const approximateWidth = Math.max(
    text.sizePt,
    shown.length * text.sizePt * 0.58,
  );
  const calloutPadding = text.callout.paddingPt * shape.ptScale;
  const calloutX =
    anchor === 'middle'
      ? tx - approximateWidth / 2 - calloutPadding
      : anchor === 'end'
        ? tx - approximateWidth - calloutPadding
        : tx - calloutPadding;
  const common = {
    x: tx,
    y: ty,
    fontSize: text.sizePt,
    fontFamily: text.fontFamily,
    fontWeight: weight,
    fontStyle,
    textAnchor: anchor,
    dominantBaseline,
    textDecoration: decoration,
    opacity: text.opacityPercent / 100,
    style: {
      fontVariant: text.textCase === 'smallCaps' ? 'small-caps' : undefined,
    },
  } as const;

  return (
    <g transform={`rotate(${text.angle} ${tx} ${ty})`}>
      {text.callout.enabled ? (
        <rect
          x={calloutX}
          y={ty - text.sizePt - calloutPadding}
          width={approximateWidth + calloutPadding * 2}
          height={text.sizePt * 1.35 + calloutPadding * 2}
          rx={calloutPadding}
          fill={text.callout.fillColor}
          stroke={text.callout.outlineColor}
          strokeWidth={Math.max(0.5, text.outlineWidthPt)}
          opacity={text.opacityPercent / 100}
        />
      ) : null}
      {text.shadow.enabled ? (
        <text
          {...common}
          x={tx + text.shadow.offsetXPt * shape.ptScale}
          y={ty + text.shadow.offsetYPt * shape.ptScale}
          fill={text.shadow.color}
          stroke="none"
        >
          {shown}
        </text>
      ) : null}
      {text.halo.enabled ? (
        <text
          {...common}
          fill={text.halo.color}
          stroke={text.halo.color}
          strokeWidth={text.halo.sizePt * 2}
          strokeLinejoin="round"
          paintOrder="stroke"
        >
          {shown}
        </text>
      ) : null}
      <text
        {...common}
        fill={text.color}
        stroke={text.outlineWidthPt > 0 ? text.outlineColor : 'none'}
        strokeWidth={text.outlineWidthPt}
        strokeLinejoin="round"
        paintOrder="stroke"
      >
        {shown}
      </text>
    </g>
  );
}

/** Division boundary x positions in view units. */
function boundaries({ geom, barW }: BarShape): number[] {
  return Array.from(
    { length: geom.divisions + 1 },
    (_, i) => (i / geom.divisions) * barW,
  );
}

function Ticks({
  shape,
  y1,
  y2,
}: {
  readonly shape: BarShape;
  readonly y1: number;
  readonly y2: number;
}) {
  return (
    <>
      {boundaries(shape).map((x, i) => (
        <line
          key={i}
          x1={x}
          y1={y1}
          x2={x}
          y2={y2}
          stroke={shape.ink}
          strokeWidth="1.2"
        />
      ))}
    </>
  );
}

function SubdivisionTicks({
  shape,
  y1,
  y2,
}: {
  readonly shape: BarShape;
  readonly y1: number;
  readonly y2: number;
}) {
  if (shape.geom.subdivisions <= 1) return null;
  const segment = shape.barW / shape.geom.divisions;
  return (
    <>
      {Array.from(
        { length: shape.geom.divisions * (shape.geom.subdivisions - 1) },
        (_, index) => {
          const division = Math.floor(index / (shape.geom.subdivisions - 1));
          const within = index % (shape.geom.subdivisions - 1) + 1;
          const x =
            division * segment
            + (within / shape.geom.subdivisions) * segment;
          return (
            <line
              key={`${division}-${within}`}
              x1={x}
              y1={y1}
              x2={x}
              y2={y2}
              stroke={shape.ink}
              strokeWidth="0.8"
            />
          );
        },
      )}
    </>
  );
}

function DivisionNumbers({
  shape,
  y,
}: {
  readonly shape: BarShape;
  readonly y: number;
}) {
  return (
    <>
      {boundaries(shape).map((x, i) => (
        <ScaleText
          key={i}
          shape={shape}
          x={x}
          y={y}
          defaultAnchor={i === 0 ? 'start' : 'middle'}
          value={shape.geom.divisionLabels[i]}
        />
      ))}
    </>
  );
}

/** Alternating boxes; `offset` flips which divisions are filled. */
function Boxes({
  shape,
  y,
  height,
  steps,
  offset = 0,
  hollow = false,
}: {
  readonly shape: BarShape;
  readonly y: number;
  readonly height: number;
  readonly steps: number;
  readonly offset?: number;
  readonly hollow?: boolean;
}) {
  const width = shape.barW / steps;
  return (
    <>
      {Array.from({ length: steps }, (_, i) => (
        <rect
          key={i}
          x={i * width}
          y={y}
          width={width}
          height={height}
          fill={hollow || (i + offset) % 2 === 1 ? shape.paper : shape.ink}
          stroke={shape.ink}
          strokeWidth="1"
        />
      ))}
    </>
  );
}

function SteppedLine({ shape }: { readonly shape: BarShape }) {
  const xs = boundaries(shape);
  const low = 28;
  const high = 20;
  let path = `M0 ${low}`;
  xs.slice(1).forEach((x, i) => {
    const y = i % 2 === 0 ? high : low;
    path += ` V${y} H${x}`;
  });
  path += ` V${low}`;
  return <path d={path} fill="none" stroke={shape.ink} strokeWidth="1.4" />;
}

function StyleMarks({
  styleId,
  shape,
}: {
  readonly styleId: ScaleBarStyleId;
  readonly shape: BarShape;
}) {
  switch (styleId) {
    case 'scaleLine1':
      return (
        <>
          <DivisionNumbers shape={shape} y={17} />
          <line x1="0" y1="24" x2={shape.barW} y2="24" stroke={shape.ink} strokeWidth="1.6" />
          <Ticks shape={shape} y1={24} y2={31} />
          <SubdivisionTicks shape={shape} y1={24} y2={28} />
        </>
      );
    case 'scaleLine2':
      return (
        <>
          <DivisionNumbers shape={shape} y={17} />
          <line x1="0" y1="26" x2={shape.barW} y2="26" stroke={shape.ink} strokeWidth="1.6" />
          <Ticks shape={shape} y1={21} y2={31} />
          <SubdivisionTicks shape={shape} y1={23} y2={29} />
        </>
      );
    case 'scaleLine3':
      return (
        <>
          <line x1="0" y1="26" x2={shape.barW} y2="26" stroke={shape.ink} strokeWidth="1.6" />
          <Ticks shape={shape} y1={18} y2={26} />
          <SubdivisionTicks shape={shape} y1={21} y2={26} />
          <DivisionNumbers shape={shape} y={38} />
        </>
      );
    case 'stepped':
      return (
        <>
          <SteppedLine shape={shape} />
          <DivisionNumbers shape={shape} y={40} />
        </>
      );
    case 'alternating1':
      return (
        <>
          <DivisionNumbers shape={shape} y={14} />
          <Boxes shape={shape} y={18} height={10} steps={shape.geom.divisions} />
        </>
      );
    case 'alternating2':
      return (
        <>
          <Boxes shape={shape} y={18} height={6} steps={shape.geom.divisions} />
          <DivisionNumbers shape={shape} y={36} />
        </>
      );
    case 'doubleAlternating1':
      return (
        <>
          <DivisionNumbers shape={shape} y={14} />
          <Boxes shape={shape} y={18} height={5} steps={shape.geom.divisions} />
          <Boxes shape={shape} y={23} height={5} steps={shape.geom.divisions} offset={1} />
        </>
      );
    case 'doubleAlternating2':
      return (
        <>
          <DivisionNumbers shape={shape} y={14} />
          <Boxes shape={shape} y={18} height={5} steps={shape.geom.divisions * 2} />
          <Boxes
            shape={shape}
            y={23}
            height={5}
            steps={shape.geom.divisions * 2}
            offset={1}
          />
        </>
      );
    case 'hollow1':
      return (
        <>
          <DivisionNumbers shape={shape} y={14} />
          <Boxes shape={shape} y={18} height={10} steps={shape.geom.divisions} hollow />
          {boundaries(shape)
            .slice(0, -1)
            .map((x, i) =>
              i % 2 === 0 ? (
                <rect
                  key={i}
                  x={x + 1}
                  y={22}
                  width={Math.max(0, shape.barW / shape.geom.divisions - 2)}
                  height={4}
                  fill={shape.ink}
                />
              ) : null,
            )}
        </>
      );
    case 'hollow2':
      return (
        <>
          <DivisionNumbers shape={shape} y={14} />
          <Boxes shape={shape} y={19} height={8} steps={shape.geom.divisions} hollow />
          <line
            x1="0"
            y1="23"
            x2={shape.barW}
            y2="23"
            stroke={shape.ink}
            strokeWidth="1"
          />
        </>
      );
    case 'singleDivision':
    default:
      return (
        <>
          <ScaleText
            shape={shape}
            x={shape.barW / 2}
            y={14}
            value={shape.geom.divisionLabels[shape.geom.divisionLabels.length - 1]}
          />
          <Boxes shape={shape} y={18} height={9} steps={1} hollow />
        </>
      );
  }
}

function DualRow({
  shape,
  family,
  y,
  labelsAbove,
}: {
  readonly shape: BarShape;
  readonly family: 'line' | 'bar';
  readonly y: number;
  readonly labelsAbove: boolean;
}) {
  const labelY = labelsAbove ? y - 5 : y + 18;
  return (
    <g>
      <DivisionNumbers shape={shape} y={labelY} />
      {family === 'bar' ? (
        <Boxes
          shape={shape}
          y={y}
          height={7}
          steps={shape.geom.divisions}
        />
      ) : (
        <>
          <line
            x1="0"
            y1={y + 4}
            x2={shape.barW}
            y2={y + 4}
            stroke={shape.ink}
            strokeWidth="1.4"
          />
          <Ticks shape={shape} y1={y} y2={y + 9} />
          <SubdivisionTicks shape={shape} y1={y + 3} y2={y + 8} />
        </>
      )}
      <ScaleText
        shape={shape}
        x={shape.barW + 4}
        y={y + 7}
        defaultAnchor="start"
        value={shape.geom.unitLabel}
      />
    </g>
  );
}

export function LayoutScaleBar({
  styleId,
  units,
  scaleDenominator,
  maxWidthMm,
  element,
  heightMm,
}: LayoutScaleBarProps) {
  const meta = scaleBarStyleMeta(styleId);
  const elementGeometry = element
    ? scaleBarElementLayout(element, scaleDenominator)
    : null;
  const geom = elementGeometry?.upper
    ?? scaleBarLayout(scaleDenominator, maxWidthMm, units, meta.divisions);
  const lowerGeom = elementGeometry?.lower ?? null;

  if (!geom) {
    return (
      <div
        style={{
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.muted,
        }}
      >
        Scale unavailable
      </div>
    );
  }

  // The drawn bar must be exactly as long as the ground distance it claims, so
  // it is a true fraction of the element width — never stretched to fill it.
  const shape: BarShape = {
    geom,
    barW: (geom.widthMm / maxWidthMm) * VIEW_W,
    ink: tokens.color.ink.inverse,
    paper: tokens.color.canvas.light,
    text: {
      ...DEFAULT_SCALE_BAR_TEXT_SYMBOL,
      ...element?.textSymbol,
      halo: {
        ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.halo,
        ...element?.textSymbol?.halo,
      },
      shadow: {
        ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.shadow,
        ...element?.textSymbol?.shadow,
      },
      callout: {
        ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.callout,
        ...element?.textSymbol?.callout,
      },
    },
    ptScale: (25.4 / 72) * (VIEW_W / maxWidthMm),
  };
  const lowerShape: BarShape | null = lowerGeom
    ? {
        ...shape,
        geom: lowerGeom,
        barW: (lowerGeom.widthMm / maxWidthMm) * VIEW_W,
      }
    : null;

  // Matching the view box aspect to the element's means the horizontal scale is
  // exactly width/VIEW_W, so `barW` lands on the paper at `geom.widthMm`.
  const contentHeight = lowerShape ? 64 : VIEW_H;
  const viewH =
    heightMm && heightMm > 0
      ? (VIEW_W * heightMm) / maxWidthMm
      : contentHeight;
  const offsetX = element
    ? (element.horizontalOffsetPt / 72) * 25.4 * (VIEW_W / maxWidthMm)
    : 0;
  const offsetY = element
    ? (element.verticalOffsetPt / 72) * 25.4 * (VIEW_W / maxWidthMm)
    : 0;
  const aria = lowerShape
    ? `Dual scale bar ${geom.unitLabel} and ${lowerShape.geom.unitLabel}`
    : `Scale bar ${geom.divisionLabels[geom.divisionLabels.length - 1]} ${geom.unitLabel}`;

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${viewH}`}
      width="100%"
      height="100%"
      aria-label={aria}
      data-scale-bar-mode={lowerShape ? 'dual' : 'single'}
      data-upper-unit={geom.unit}
      data-lower-unit={lowerShape?.geom.unit}
      preserveAspectRatio="xMinYMid meet"
      style={{ fontFamily: tokens.font.family.ui, overflow: 'visible' }}
    >
      <g
        transform={`translate(${offsetX} ${
          (viewH - contentHeight) / 2 + offsetY
        })`}
      >
        {lowerShape ? (
          <>
            <DualRow shape={shape} family={meta.family} y={15} labelsAbove />
            <DualRow
              shape={lowerShape}
              family={meta.family}
              y={40}
              labelsAbove={false}
            />
          </>
        ) : (
          <>
            <StyleMarks styleId={styleId} shape={shape} />
            <ScaleText
              shape={shape}
              x={shape.barW + 4}
              y={26}
              defaultAnchor="start"
              value={geom.unitLabel}
            />
          </>
        )}
      </g>
    </svg>
  );
}
